<script setup lang="ts">
/**
 * Footer V2 — Compliance bar (Mobile)
 *
 * Mesma origem de dados do footer legado (components/footer/Emails.vue):
 *   - Ouvidoria          → brandContacts.customer_service.link
 *   - Denúncias          → brandContacts.complaints.link
 *   - Suporte ao Jogador → brandContacts.support.link
 *   - E-mail             → brandContacts.support.email
 * Textos via i18n: footer.customer_service_office / footer.complaints /
 * footer.support_player / footer.support.
 *
 * Layout: 3 itens em linha (wrap se faltar espaço) + frase "Suporte via
 * e-mail" + e-mail clicável em linha própria.
 */
const { t } = useTexts();
const { brandContacts } = useFeatureFlagsNew();
const { supportEmail, legalEmail, partnerEmail } = useSupportEmail();

const customerServiceLink = computed<string>(
  () => brandContacts.value?.customer_service?.link || '',
);

const complaintsLink = computed<string>(
  () => brandContacts.value?.complaints?.link || '',
);

const supportLink = computed<string>(
  () => brandContacts.value?.support?.link || '',
);

const topLinks = computed(() =>
  [
    {
      label: t('footer.customer_service_office'),
      href: customerServiceLink.value,
    },
    { label: t('footer.complaints'), href: complaintsLink.value },
    { label: t('footer.support_player'), href: supportLink.value },
  ].filter((l) => !!l.href),
);
</script>

<template>
  <div
    v-if="topLinks.length || supportEmail || legalEmail || partnerEmail"
    class="footer-v2-compliance lg:hidden"
  >
    <a
      v-for="item in topLinks"
      :key="item.label"
      class="footer-v2-compliance__item"
      :href="item.href"
      rel="noopener noreferrer"
      target="_blank"
    >
      {{ item.label }}
    </a>

    <span
      v-if="supportEmail"
      class="footer-v2-compliance__item footer-v2-compliance__email-line"
    >
      {{ t('footer.support') }} via e-mail
      <a
        class="footer-v2-compliance__email lowercase"
        :href="`mailto:${supportEmail}`"
      >
        {{ supportEmail }}
      </a>
    </span>

    <span
      v-if="legalEmail"
      class="footer-v2-compliance__item footer-v2-compliance__email-line"
    >
      {{ t('footer.legal') }}
      <a
        class="footer-v2-compliance__email"
        :href="`mailto:${legalEmail}`"
      >
        {{ legalEmail }}
      </a>
    </span>

    <span
      v-if="partnerEmail"
      class="footer-v2-compliance__item footer-v2-compliance__email-line"
    >
      {{ t('footer.partners') }}
      <a
        class="footer-v2-compliance__email"
        :href="`mailto:${partnerEmail}`"
      >
        {{ partnerEmail }}
      </a>
    </span>
  </div>
</template>

<style scoped lang="scss">
.footer-v2-compliance {
  display: flex;
  width: 100%;
  max-width: 353px;
  /* O pai (.footer-v2) é flex-col com align-items: stretch (default).
     Como temos largura fixa, sem align-self: center o bloco encosta
     na esquerda do container e fica visualmente desalinhado dos
     demais blocos (que usam align-self: stretch). */
  align-self: center;
  justify-content: center;
  align-items: center;
  align-content: center;
  gap: 5px 20px;
  flex-wrap: wrap;

  &__item {
    color: rgba(255, 255, 255, 0.5); // var(--White50)
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 21px; /* 175% */
    text-decoration: none;
  }

  &__email-line {
    /* Quebra inteira na 2ª linha conforme Figma */
    flex-basis: 100%;
    text-align: center;
  }

  &__email {
    color: #fff;
    text-decoration: none;
  }
}
</style>
